import axios from 'axios';
import store from '@/stores/store';
import { ModuleContext } from '@/stores/moduleConst';
import { showError } from '@/commons/globalMessage';
import commonFn from '@/commons/commonFunction';
import auth from '@/commons/authService';
import { useRefreshToken} from '@/composables/refreshToken/useRefreshToken';
const { setAuthData } = useRefreshToken();

// Create an Axios instance
const api = axios.create();

// Biến kiểm tra xem có đang thực hiện refresh token không
let isRefreshing = false;
// Biến lưu trữ promise của refresh token
let refreshTokenPromise = null;
// Danh sách request đang chờ refresh token
let failedQueue = [];
// Danh sách callback đang chờ refresh token
let refreshSubscribers = [];
// Biến kiểm tra refresh token thất bại
let isRefreshTokenFalse = false;

// Interceptor request - Kiểm tra token trước khi gửi request
api.interceptors.request.use(
  async (config) => {
    // Kiểm tra nếu token đã hết hạn
    var checkExpiredToken = config?.isCheckExpiredToken ?? true;
    if (!auth.isAuthenticated() && checkExpiredToken) {
      // Nếu refresh token thất bại trước đó, không thực hiện refresh nữa
      if (isRefreshTokenFalse) {
        return Promise.reject('Refresh token failed');
      }

      // Nếu đang refresh, chờ đến khi refresh xong rồi tiếp tục request
      if (isRefreshing) {
        return new Promise((resolve) => {
            refreshSubscribers.push((newToken) => {
              config.headers["Authorization"] = newToken;
              resolve(config);
          });
        });
      }

      // Nếu không có request nào đang refresh, tiến hành refresh
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();

        // Cập nhật token mới cho tất cả request đang chờ
        onRefreshed(newToken);
        isRefreshing = false;

        config.headers["Authorization"] = newToken;
        return config;
      } catch (error) {
        isRefreshing = false;
        refreshSubscribers = [];
        return Promise.reject(error);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Function to handle refreshed token
function onRefreshed(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
  isRefreshTokenFalse = false; // Reset cờ khi token được cập nhật thành công
}

// Function to process queued requests
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Response Interceptor: Handle 401 and retry
api.interceptors.response.use(
  (response) => response, // Pass successful responses
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status === 401 && !originalRequest?._retry) {
      // Nếu refresh token thất bại trước đó, không thực hiện refresh nữa
      if (isRefreshTokenFalse) {
        return Promise.reject('Refresh token failed');
      }
      
      if (isRefreshing) {
        try {
          const newToken = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          originalRequest.headers['Authorization'] = `${newToken}`;
          return api(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;
      refreshTokenPromise = refreshAccessToken(); // Call refresh token function

      try {
        const newToken = await refreshTokenPromise;
        processQueue(null, newToken);
        originalRequest.headers['Authorization'] = `${newToken}`;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
        refreshTokenPromise = null;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Hàm thực hiện refresh token 
 */
async function refreshAccessToken() {
  try {
    const context = store.state[ModuleContext];

    if (!context || !context.Token || !context.RefreshToken) {
      throw new Error('No token or refresh token');
    }

    const response = await axios.get(window._apis.Auth+'/Accounts/token-refreshment', {  
      headers: {
        'Authorization': context.Token,
        'RefreshToken': context.RefreshToken,
      }
    });

    if (!response.data || typeof response.data !== 'object') { 
      throw new Error('Invalid refresh token response');
    }

    if (!response.data?.Success) {
      throw new Error(response.data?.ErrorMessage);
    }

    store.dispatch(`${ModuleContext}/refreshToken`, response.data);

    // set auth data vào local storage để cập nhật sang tab khác
    setAuthData(response.data);
    
    return response.data.AccessToken;
  } catch (error) {
    commonFn.unmask();
    isRefreshTokenFalse = true;
    showError('Phiên làm việc đã kết thúc.').then(() => {
      commonFn.logoff();
    });
    throw error;
  }
}

export default function axiosReq(config) {
  return api(config);
}
